import { useState, useEffect, useCallback } from 'react';
import { fallanalyseAPI } from '@/lib/api';
import {
  ChevronDown, ChevronUp, ShieldCheck, ExternalLink,
  BookOpen, Loader2, Info,
} from 'lucide-react';

export function VertrauenshinweiseView({ entwurfId, lbTyp, t }) {
  const [open, setOpen] = useState(false);
  const [daten, setDaten] = useState(null);
  const [laden, setLaden] = useState(false);

  const istVerfuegbar = lbTyp === 'ki_lagebeurteilung';

  const ladeDaten = useCallback(async () => {
    if (!istVerfuegbar || daten) return;
    setLaden(true);
    try {
      const res = await fallanalyseAPI.entwurfVertrauenshinweise(entwurfId);
      setDaten(res.data);
    } catch { /* silent */ }
    finally { setLaden(false); }
  }, [entwurfId, istVerfuegbar, daten]);

  useEffect(() => {
    if (open && !daten && istVerfuegbar) ladeDaten();
  }, [open, ladeDaten, daten, istVerfuegbar]);

  if (!istVerfuegbar) return null;

  return (
    <div className="mt-4" data-testid="vertrauenshinweise-bereich">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors w-full text-left"
        data-testid="vertrauenshinweise-toggle"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        <ShieldCheck className="h-4 w-4 text-primary" />
        {t('vertrauen.titel')}
      </button>

      {open && (
        <div className="mt-3 space-y-3" data-testid="vertrauenshinweise-inhalt">
          {laden ? (
            <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('vertrauen.laden')}
            </div>
          ) : daten ? (
            <>
              {/* Abgrenzung (immer sichtbar, kompakt) */}
              <div className="p-3 rounded-lg border border-slate-200 bg-slate-50/40" data-testid="abgrenzung">
                <div className="flex items-start gap-2.5">
                  <Info className="h-3.5 w-3.5 text-slate-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-600 leading-relaxed">{daten.abgrenzung_kurz}</p>
                </div>
              </div>

              {/* Grundlagen (kompakte Liste) */}
              {daten.grundlagen?.length > 0 && (
                <div data-testid="grundlagen">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <BookOpen className="h-3 w-3" />
                    {t('vertrauen.grundlagenTitel')}
                  </p>
                  <div className="space-y-1">
                    {daten.grundlagen.map((g, i) => (
                      <p key={i} className="text-xs text-foreground leading-relaxed pl-4 relative before:content-['·'] before:absolute before:left-1 before:text-primary">
                        {g}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Externe Anlaufstellen (nur wenn vorhanden) */}
              {daten.anlaufstellen?.length > 0 && (
                <div data-testid="anlaufstellen">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                    <ExternalLink className="h-3 w-3" />
                    {t('vertrauen.anlaufstellenTitel')}
                  </p>
                  <div className="space-y-2">
                    {daten.anlaufstellen.map((a, i) => (
                      <div key={i} className="p-2.5 rounded-lg border border-warm-200 bg-warm-50/20" data-testid={`anlaufstelle-${i}`}>
                        <p className="text-xs font-medium text-foreground">{a.bezeichnung}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{a.wann}</p>
                        <p className="text-[11px] text-primary/70 mt-0.5">{a.wie}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
